import { useEffect, useRef } from "react";
import { motion, useAnimationControls } from "motion/react";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import { useNavigate } from "react-router";
import { useNavigation } from "@/pages/home/api";
import { useGetVouchers } from "@/pages/loyalty/api";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import { hapticFeedback } from "@telegram-apps/sdk-react";
import { getImageByPriority } from "@/shared/utils/common";
import IconShoppingCart from "@/assets/icons/shopping-cart.svg?react";
import ArrowRightLeft from "@/assets/icons/arrows-right-left.svg?react";
import IconNotes from "@/assets/icons/notes.svg?react";
import IconVoucher from "@/assets/icons/voucher.svg?react";
import IconUsers from "@/assets/icons/partner.svg?react";
import UserPlusIcon from "@/assets/icons/user-plus.svg?react";
import { useTranslation } from "react-i18next";

// Bundled icons keyed by HomePageWidget.key so admins can add a widget
// with just a key + title and the mini-app picks the matching icon. If
// the admin uploads an `image` the image wins over this map; if neither
// matches we still render a generic shopping-cart placeholder.
const ICON_BY_KEY: Record<string, React.ReactNode> = {
  p2p: <ArrowRightLeft className="w-5 h-5" />,
  surveys: <IconNotes className="w-5 h-5" />,
  vouchers: <IconVoucher className="w-6 h-6" />,
  partners: <IconUsers className="w-6 h-6" />,
  referral: <UserPlusIcon className="w-5 h-5" />,
  shop: <IconShoppingCart className="w-5 h-5 text-white/60" />,
};

const SectionNavigation = ({ className }: { className?: string }) => {
  // Uncached widget list (HomePageWidgetList) so admin widget changes / staged
  // releases appear immediately, not after the 24h /homepage/Homepage cache.
  const { data, isLoading } = useNavigation();
  const { data: vouchers } = useGetVouchers();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isDraggingRef = useRef(false);
  const nudgeControls = useAnimationControls();

  const redeemedVoucherCount =
    vouchers?.filter((v) => v.status === "REDEEMED").length ?? 0;

  // All home tiles come from the backend HomePageWidget list (admin-controlled),
  // exactly as before. Education is NOT a home tile — it lives inside its
  // get-don collection ("startup") as a section card, so it's not added here.
  const allItems = ((data as any[]) ?? []).map((el: any) => {
    const key = el.key as string | undefined;
    const link =
      (el.link as string | null) || (key === "p2p" ? "/p2p" : null);
    return {
      key,
      title: el.title,
      link,
      image: el.image,
      icon: !el.image && key ? ICON_BY_KEY[key] : undefined,
      comingSoon: !link,
    };
  });

  const fireHaptic = () => {
    if (hapticFeedback.impactOccurred.isAvailable()) {
      hapticFeedback.impactOccurred("medium");
    } else if (hapticFeedback.selectionChanged.isAvailable()) {
      hapticFeedback.selectionChanged();
    }
  };

  const canScroll = allItems.length > 3;

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: false,
    mode: "free-snap",
    slides: {
      perView: 3,
      spacing: 8,
    },
    dragSpeed: 1,
    rubberband: false,
    dragStarted: () => {
      isDraggingRef.current = true;
    },
    dragEnded: () => {
      isDraggingRef.current = false;
    },
    slideChanged: () => {
      fireHaptic();
    },
  });

  // App ochilganda/reload'da slider bir marta sekin chapga surilib qaytadi
  // (peek) — keyingi widgetning logosi va yozuvi taxminan yarmigacha
  // ko'rinadi, keyin ortga qaytadi. Foydalanuvchiga yana bo'lim borligini
  // va surish mumkinligini bildirish uchun. Faqat ko'rinishga sig'maydigan
  // (scroll bo'ladigan) holatda ishlaydi (perView=3).
  useEffect(() => {
    if (isLoading || !canScroll) return;
    const timer = setTimeout(() => {
      // Keyingi widgetni yarmigacha ochish uchun: bitta slide kengligining
      // yarmi + slide'lar orasidagi bo'shliq (spacing 8).
      const slide = instanceRef.current?.container.querySelector<HTMLElement>(
        ".keen-slider__slide"
      );
      const shift = slide ? slide.offsetWidth / 2 + 8 : 60;
      nudgeControls.start({
        x: [0, -shift, 0],
        transition: { duration: 1, times: [0, 0.45, 1], ease: "easeInOut" },
      });
    }, 600);
    return () => clearTimeout(timer);
  }, [isLoading, canScroll, nudgeControls, instanceRef]);

  return (
    <div className={`overflow-x-clip overflow-y-visible ${className ?? ""}`}>
      {isLoading ? (
        <div className="grid grid-cols-3 gap-2 container">
          {Array.from({ length: 3 }).map((_, idx) => (
            <div
              key={idx}
              className="px-3 py-2 flex items-center flex-col bg-white/08 rounded-xl border-b border-white/08"
            >
              <div className="w-8 h-8 rounded-full skeleton mb-2" />
              <div className="skeleton rounded h-3 w-3/5" />
            </div>
          ))}
        </div>
      ) : (
        <motion.div className="container" animate={nudgeControls}>
          <div ref={sliderRef} className="keen-slider !overflow-visible">
            {allItems.map((el, index) => (
              <div key={index} className="keen-slider__slide !overflow-visible">
                <VibratingButton
                  disabled={!!el.comingSoon}
                  onClick={() => {
                    if (isDraggingRef.current) return;
                    if (el.link) navigate(el.link);
                  }}
                  className="px-3 py-2 w-full !flex items-center flex-col bg-white/08 rounded-xl border-b border-white/08 transition-all duration-100 click-button relative"
                >
                  {el.key === "vouchers" && redeemedVoucherCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 z-20 min-w-[20px] h-5 px-1.5 rounded-full bg-red text-white text-[10px] font-bold leading-none flex-center border border-white/08">
                      {redeemedVoucherCount > 99 ? "99+" : redeemedVoucherCount}
                    </span>
                  )}
                  {el.comingSoon && (
                    <div
                      className="absolute inset-0 rounded-xl bg-black/20 z-10 flex items-center justify-center"
                      style={{
                        backdropFilter: "blur(1px)",
                        WebkitBackdropFilter: "blur(1px)",
                      }}
                    >
                      <span className="py-0.5 px-2 text-[10px] font-semibold rounded-full bg-orange-100/15 text-orange-100 leading-130 border border-orange-100/20 whitespace-nowrap">
                        {t("soon").replace(/\./g, "")}
                      </span>
                    </div>
                  )}
                  <div className="w-8 h-8 bg-white/08 rounded-full flex-center mb-2">
                    {el.comingSoon ? (
                      <IconShoppingCart className="w-5 h-5 text-white/60" />
                    ) : el.icon ? (
                      el.icon
                    ) : el.image ? (
                      <img
                        className="w-5 h-5 object-contain"
                        src={getImageByPriority(el.image, "medium")}
                        alt={el.title}
                      />
                    ) : null}
                  </div>
                  <p className="text-white !whitespace-nowrap text-xs font-medium leading-130">
                    {el.title}
                  </p>
                </VibratingButton>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};



export default SectionNavigation;
